import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getDB } from "../services/storageService"; 

const BatchContext = createContext();
export const useBatch = () => useContext(BatchContext);

export const BatchProvider = ({ children }) => {
  const [activeBatches, setActiveBatches] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const siteKey = process.env.REACT_APP_SITE_KEY;

  /**
   * REFRESH LOCAL STATE
   * Pulls the latest from Dexie to show in the BatchMonitorView.
   */
  const refreshAllBatches = useCallback(async () => {
    try {
      if (!siteKey) return;
      const db = getDB(siteKey);
      const saved = await db.batches.toArray();
      setActiveBatches(saved);
    } catch (err) {
      console.error("Dexie Hydration Error:", err);
    }
  }, [siteKey]);

  useEffect(() => {
    refreshAllBatches();
  }, [refreshAllBatches]);

  /**
   * ⚡ THE TELEMETRY PULSE
   * This drives the real-time animation in BatchMonitorView.
   * Functional updates prevent React from dropping high-frequency pulses.
   */
  const handleProgress = useCallback((batchId, fileName, percent) => {
    // 1. Update the flat percentage map for text labels
    setUploadProgress(prev => {
      if (prev[`${batchId}-${fileName}`] === percent) return prev;
      return { ...prev, [`${batchId}-${fileName}`]: percent };
    });

    // 2. Update activeBatches state
    setActiveBatches(prevBatches => prevBatches.map(batch => {
      if (batch.batchId !== batchId) return batch;

      const updatedFiles = (batch.files || []).map(f => {
        if (f.name === fileName) {
          if (f.progress === percent) return f;
          return { ...f, progress: percent, status: percent === 100 ? 'Delivered' : 'Active' };
        }
        return f;
      });

      // 🎯 THE COMPLETION TRIGGER
      // If a file hits 100%, we pulse a refresh to pull the Finalized status from Dexie
      if (percent === 100) {
        setTimeout(() => refreshAllBatches(), 500); 
      }

      return { ...batch, files: updatedFiles };
    }));
  }, [refreshAllBatches]); // 🚀 REQUIRED DEPENDENCY

  return (
    <BatchContext.Provider
      value={{
        activeBatches,
        uploadProgress,
        handleProgress,
        refreshAllBatches,
      }}
    >
      {children}
    </BatchContext.Provider>
  );
};